import React from "react";
import { Box } from "@mui/material";
import { certificatePageSize } from "../../../domain/certificates/certificateTemplates";
import {
  CentredLayout,
  certificateInk,
  type CertificateDesign,
} from "./certificateParts";

/** How thick the arches and rings are drawn. */
const BOLD = 14;
const RING = 8;

/** An arch of radius r round (cx, cy): its top half, or with `down`, its bottom half. */
function arch(cx: number, cy: number, r: number, down = false): string {
  return `M${cx - r},${cy} A${r},${r} 0 0 ${down ? 0 : 1} ${cx + r},${cy}`;
}

/** Rings round a point, each `step` wider than the last. */
const Rings: React.FC<{
  cx: number;
  cy: number;
  from: number;
  step: number;
  count: number;
  colour: string;
}> = ({ cx, cy, from, step, count, colour }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <circle
        key={i}
        cx={cx}
        cy={cy}
        r={from + step * i}
        fill="none"
        stroke={colour}
        strokeWidth={RING}
      />
    ))}
  </>
);

/**
 * Blue shapes round the edges of the page, placed by its size so they land
 * the same way round in either orientation, with a scatter of dots in the
 * accent colour.
 */
const Shapes: React.FC<{
  width: number;
  height: number;
  primary: string;
  accent: string;
}> = ({ width: w, height: h, primary, accent }) => (
  <g fill="none" stroke={primary} strokeLinecap="butt">
    {/* Top left: a square outline, and a bar. */}
    <rect x={24} y={20} width={56} height={56} strokeWidth={6} />
    <rect x={w * 0.13} y={-10} width={22} height={58} fill={primary} stroke="none" />
    {/* Top: an arch hanging from the edge, and a small ring. */}
    <path d={arch(w * 0.36, 0, 32, true)} strokeWidth={BOLD} />
    <circle cx={w * 0.6} cy={34} r={14} strokeWidth={6} />
    {/* Top right: rings round the corner. */}
    <Rings cx={w - 30} cy={20} from={34} step={22} count={3} colour={primary} />
    {/* Left: a thick curve bulging in from the edge, and a bar. */}
    <path d={arch(-10, h * 0.42, 76, false)} transform={`rotate(90 -10 ${h * 0.42})`} strokeWidth={26} />
    <rect x={26} y={h * 0.62} width={14} height={74} fill={primary} stroke="none" />
    {/* Right: an arch and a bar off the edge. */}
    <path d={arch(w - 52, h * 0.42, 24)} strokeWidth={BOLD} />
    <rect x={w - 58} y={h * 0.56} width={70} height={26} fill={primary} stroke="none" />
    {/* Bottom left: rings round the corner. */}
    <Rings cx={36} cy={h + 6} from={40} step={22} count={3} colour={primary} />
    {/* Bottom: an arch standing on the edge, a square and a bar. */}
    <path d={arch(w * 0.3, h, 30)} strokeWidth={BOLD} />
    <rect x={w * 0.68} y={h - 46} width={34} height={34} strokeWidth={6} />
    <rect x={w * 0.76} y={h - 64} width={22} height={74} fill={primary} stroke="none" />
    {/* Bottom right: a quarter of large rings. */}
    <Rings cx={w + 10} cy={h + 10} from={58} step={24} count={3} colour={primary} />
    {/* Dots: a few large in the accent colour, a few small in the primary. */}
    <g stroke="none">
      {[
        [0.06, 0.2, 8],
        [0.24, 0.07, 6],
        [0.9, 0.3, 7],
        [0.05, 0.86, 7],
        [0.47, 0.95, 6],
        [0.94, 0.66, 9],
        [0.84, 0.94, 5],
      ].map(([x, y, r]) => (
        <circle key={`${x},${y}`} cx={w * x} cy={h * y} r={r} fill={accent} />
      ))}
      {[
        [0.5, 0.05, 5],
        [0.09, 0.32, 5],
        [0.95, 0.85, 4],
        [0.18, 0.95, 5],
      ].map(([x, y, r]) => (
        <circle key={`${x},${y}`} cx={w * x} cy={h * y} r={r} fill={primary} />
      ))}
    </g>
  </g>
);

/**
 * Geometric: bold blue arches, rings and bars round a dark page, with dots in
 * the accent colour, like a modern certificate of appreciation. The title is
 * in the light ink and the Awardee's name in a script in the accent colour.
 */
export const geometricDesign: CertificateDesign = {
  bodyFont: "montserrat",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        lettering={{
          titleColour: certificateInk(template).ink,
          nameFont: "great-vibes",
          nameColour: accent,
        }}
        inset={{
          landscape: "92px 150px 84px",
          portrait: "120px 100px 120px",
        }}
        frame={
          <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width, height }}
          >
            <Shapes width={width} height={height} primary={primary} accent={accent} />
          </Box>
        }
      />
    );
  },
};
